import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import ServiceIcon from "@/components/ServiceIcon";
import { ISOLifecycleDiagram } from "@/components/Diagrams";
import { COMPANY, HERITAGE_CLIENTS } from "@/config/company";
import { VALUES, INDUSTRIES } from "@/data/site";

export default function About() {
  return (
    <>
      <SEO
        title="About Us"
        description="Learn about Numeric Consulting & Solutions — our story, mission, values and the 40+ years of collective experience behind our management-systems, security and risk consulting practice."
        path="/about"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "About Us", path: "/about" }]}
      />
      <PageHero
        eyebrow="About Us"
        title="Consulting Built on Experience, Discipline and Practicality"
        description="A management systems and ISO standards consulting company helping organizations strengthen governance, protect information assets and build resilience."
        breadcrumb="About Us"
      />

      {/* Company story */}
      <section className="bg-white py-16 sm:py-24" data-testid="about-story">
        <div className="container-site grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <p className="eyebrow mb-4">Our Story</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-navy">
              From Technology Consulting to Management-Systems Expertise
            </h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-muted">
              <p>
                {COMPANY.name} is a management systems and ISO standards consulting company specializing in
                compliance, information security, cybersecurity, business continuity, quality management,
                IT governance, risk advisory and application development.
              </p>
              <p>
                The company is an offshoot of {COMPANY.parentName}, an IT consulting and third-party
                technology products and services company operating since {COMPANY.parentSince}. That heritage
                matters: our recommendations are shaped by years spent inside real technology environments,
                working with the systems, vendors and operational constraints our clients live with every day.
              </p>
              <p>
                Our team brings more than {COMPANY.experienceYears} years of collective experience across
                consulting, compliance and risk advisory — experience that shows up in practical, implementable
                work rather than theoretical reports.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-2xl border border-slate-200 bg-mist p-7 sm:p-8">
              <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-corp mb-5">
                {COMPANY.parentName} — Historic Client Base
              </h3>
              <ul className="space-y-3">
                {HERITAGE_CLIENTS.map((c) => (
                  <li key={c} className="flex items-center gap-2.5 text-sm font-medium text-ink">
                    <span className="w-1.5 h-1.5 rounded-full bg-teal shrink-0" aria-hidden="true" />
                    {c}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs leading-relaxed text-muted border-t border-slate-200 pt-4">
                Organizations listed represent the broader client history provided for {COMPANY.parentName}.
                Logos and endorsements are not implied.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Mission & vision */}
      <section className="bg-mist py-16 sm:py-24" data-testid="about-mission">
        <div className="container-site grid md:grid-cols-2 gap-6">
          <Reveal>
            <div className="h-full rounded-2xl bg-navy p-8 sm:p-10 relative overflow-hidden">
              <div className="absolute inset-0 grid-pattern-dark" aria-hidden="true" />
              <div className="relative">
                <p className="eyebrow-light mb-4">Our Mission</p>
                <p className="font-heading text-xl sm:text-2xl font-bold leading-snug text-white">
                  To help organizations build secure, resilient and effective management systems through
                  practical consulting, risk-based guidance and internationally recognized standards.
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full rounded-2xl bg-white border border-slate-200 p-8 sm:p-10">
              <p className="eyebrow mb-4">Our Vision</p>
              <p className="font-heading text-xl sm:text-2xl font-bold leading-snug text-navy">
                To be a trusted partner for organizations seeking stronger governance, improved security,
                operational resilience and sustainable performance.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Core values */}
      <section className="bg-white py-16 sm:py-24" data-testid="about-values">
        <div className="container-site">
          <Reveal className="max-w-2xl">
            <p className="eyebrow mb-4">Core Values</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">
              The Principles Behind Every Engagement
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {VALUES.map((v, i) => (
              <Reveal key={v.name} delay={(i % 3) * 0.08}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-7 hover:border-teal/40 hover:shadow-md transition-[box-shadow,border-color] duration-300">
                  <span className="font-mono text-xs font-semibold text-teal-dark" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-heading text-lg font-semibold text-ink">{v.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted">{v.detail}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Team expertise + lifecycle */}
      <section className="bg-mist py-16 sm:py-24" data-testid="about-expertise">
        <div className="container-site grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="eyebrow mb-4">Team Expertise</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-navy">
              {COMPANY.experienceYears} Years of Collective Experience
            </h2>
            <p className="mt-6 text-base leading-relaxed text-muted">
              Our consultants bring combined experience across consulting, compliance and risk advisory —
              spanning management-system implementation, security assessment, continuity planning, quality
              management and technology delivery for demanding, regulated environments.
            </p>
            <p className="mt-4 text-base leading-relaxed text-muted">
              We work as an integrated practice: the same discipline that shapes an ISO management system also
              informs how we test security controls, plan remediation and develop applications.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-2xl bg-white border border-slate-200 p-6 sm:p-8 shadow-sm">
              <ISOLifecycleDiagram />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Industries served */}
      <section className="bg-white py-16 sm:py-24" data-testid="about-industries">
        <div className="container-site">
          <Reveal className="max-w-2xl">
            <p className="eyebrow mb-4">Industries Served</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-navy">
              Where We Focus
            </h2>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.id} delay={(i % 4) * 0.06}>
                <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-mist px-5 py-4">
                  <ServiceIcon name={ind.icon} size={20} className="text-corp shrink-0" />
                  <span className="text-sm font-semibold text-ink">{ind.name}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
